import React, { useCallback, useRef, useState } from 'react';
import ModalGenerateText from 'react-modal';
import closeBtn from '../../assets/close.svg';
import { ModalResponseProps } from '../type';
import logoFB from '../../assets/review/logoFB.svg';
import logoGMB from '../../assets/review/logoGMB.svg';
import logoIA from '../../assets/review/logo-green-ia.svg';
import logoEmojiPicker from '../../assets/review/logoEmojiPicker.svg';
import StarRating from './StarRating';
import axios from 'axios';
import loader from '../../assets/loader.png';
import {
    formatDate,
    formatRecommendationDisplay,
    formatReviewText,
    getRatingNumber,
} from '../utils/formatters';
import { useSelector } from 'react-redux';
import EmojiPickerInput from '../../components/EmojiPickerInput';
import {
    selectIsSsoUser,
    selectUserEmail,
    selectUserId,
    selectUserMyId,
} from '../../selectors/userSelectors';

const ModalResponse: React.FC<ModalResponseProps> = ({
    isOpen,
    onClose,
    selectedReview,
    setContent,
    content,
    setRefreshReviews,
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [selectedTone, setSelectedTone] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isloading, setIsloading] = useState(false);
    const userId = useSelector(selectUserId);
    const userMyId = useSelector(selectUserMyId);
    const userSso = useSelector(selectIsSsoUser);
    const userEmail = useSelector(selectUserEmail);

    const availableTones = [
        { id: 'chaleureux', emoji: '🤗', label: 'Chaleureux' },
        { id: 'professionnel', emoji: '💼', label: 'Professionnel' },
        { id: 'empathique', emoji: '😌', label: 'Empathique' },
        { id: 'humoristique', emoji: '😂', label: 'Humoristique' },
        { id: 'formel', emoji: '📜', label: 'Formel' },
        { id: 'amical', emoji: '😊', label: 'Amical' },
    ];

    const getCurrentUserRequest = useCallback(
        async (user: number) => {
            try {
                const response = await axios.get(
                    userSso ? `/api/users/${user}` : `/api/user_admins/${user}`,
                );
                return response.data;
            } catch (error) {
                console.error(`Error fetching user request for user ${userEmail ?? ''}:`, error);
                throw error;
            }
        },
        [userEmail, userSso],
    );

    const handleToneSelection = async (tone: string) => {
        setSelectedTone(tone);
        if (!selectedReview) return;
        setIsGenerating(true);
        if (userId === undefined) {
            setIsGenerating(false);
            return;
        }

        try {
            const response = await axios.post('/api/generate-text-review', {
                review: selectedReview.text,
                tone: tone,
                user: userId,
                reviewId: selectedReview.id,
                network: selectedReview.network,
            });

            setContent(response.data.generated_text);

            try {
                const currentUser = await getCurrentUserRequest(userId);
                if (currentUser.requestIA < currentUser.quotaIA) {
                    await axios.patch(
                        userSso ? `/api/users/${userId}` : `/api/user_admins/${userId}`,
                        { requestIA: currentUser.requestIA + 1 },
                        { headers: { 'Content-Type': 'application/merge-patch+json' } },
                    );
                }
            } catch (error) {
                console.error('Error when update requestIa ', error);
            }
        } catch (error) {
            console.error('Error generating response:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSubmitResponse = async () => {
        if (!selectedReview || !content.trim() || userMyId === undefined) return;
        setIsloading(true);

        try {
            const response = await axios.post('/api/submit-review-response', {
                userId: userMyId,
                reviewId: selectedReview.id,
                content: content,
                network: selectedReview.network,
            });

            if (response.status === 200) {
                onClose();
                setRefreshReviews(prev => prev + 1);
            }
        } catch (error) {
            console.error('Error submitting response:', error);
        } finally {
            setIsloading(false);
        }
    };

    return (
        <ModalGenerateText
            id="root"
            className="modalGenerateText"
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Générer une réponse"
        >
            {selectedReview && (
                <div className="relative flex flex-col items-center gap-6 p-8 rounded-[24px] bg-[#FAFAFA]">
                    <span
                        className="absolute top-[16px] right-[16px] z-[10] flex items-center gap-[10px] rounded-[60px] border border-gray bg-white p-[10px] cursor-pointer"
                        onClick={onClose}
                    >
                        <img className="w-[12px] h-[12px]" src={closeBtn} alt="Fermer la fenêtre" />
                    </span>
                    <div className="relative flex flex-col gap-6 w-full">
                        <div className="flex items-center justify-start self-stretch gap-[10px]">
                            <div className="left">
                                <img
                                    className="flex items-center justify-center h-auto p-[6px] rounded-[6px] bg-primary-light w-[36x]"
                                    src={selectedReview.network === 'facebook' ? logoFB : logoGMB}
                                    alt={selectedReview.network}
                                />
                            </div>
                            <div className="right">
                                <div className="flex items-center">
                                    <div className="text-medium-title font-medium text-dark-gray">
                                        {selectedReview.reviewer}
                                    </div>
                                    <div className="text-[10px] font-small leading-[120%] text-gray ml-2">
                                        {formatDate(selectedReview.created_at)}
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    {selectedReview.network === 'google_my_business' ? (
                                        <StarRating
                                            rating={getRatingNumber(selectedReview.rating)}
                                        />
                                    ) : (
                                        formatRecommendationDisplay(selectedReview.rating)
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="text-dark-gray text-medium-small font-small leading-[120%] m-0 p-0">
                            {formatReviewText(selectedReview.text)}
                        </div>
                    </div>

                    <div className="hr" />

                    <div className="w-full flex flex-col items-start gap-2 self-stretch">
                        <div className="flex items-center gap-2">
                            <img
                                className="flex items-center gap-[10px] p-1 rounded-[6px] bg-[#D0E6C8] w-[24px] h-[24px] aspect-square"
                                src={logoIA}
                                alt="logo ia"
                            />
                            <h3 className="text-dark-gray text-medium-title font-medium leading-[120%] p-0 m-0">
                                Votre réponse boostée par l'IA
                            </h3>
                        </div>

                        <div className="text-gray text-[12px] font-small leading-[120%]">
                            Sélectionnez le ton que vous voulez pour la réponse générée par l'IA.
                        </div>

                        <div className="tone-selection">
                            <div
                                className="
                              flex
                              items-start
                              content-start
                              gap-2.5
                              self-stretch
                              flex-wrap
                            "
                            >
                                {availableTones.map(tone => (
                                    <button
                                        key={tone.id}
                                        onClick={() => handleToneSelection(tone.id)}
                                        className={`flex items-center gap-[6px] px-[12px] py-[6px] rounded-full border-solid border-[1px] border-dark-gray bg-white text-[13px] font-medium leading-[120%] text-secondary hover:border-dark-gray
hover:bg-primary-light ${selectedTone === tone.id ? 'border-primary' : ''}`}
                                    >
                                        {tone.emoji} {tone.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col items-end self-stretch min-h-[193px]">
                            <div className="relative w-full rounded-bl-none rounded-br-none text-[14px]">
                                {isGenerating ? (
                                    <div className="w-full h-[200px] flex justify-center items-center">
                                        <img
                                            src={loader}
                                            alt="Chargement..."
                                            className="mt-[15px] animate-rotate w-[25px] h-[25px]"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <textarea
                                            ref={textareaRef}
                                            className="
                                                relative
                                                w-full
                                                min-h-[150px]
                                                p-4
                                                text-medium-small
                                                leading-[1.5]
                                                resize-none
                                                rounded-bl-none
                                                rounded-br-none
                                                border-0
                                            "
                                            name="content"
                                            value={content}
                                            onChange={e => setContent(e.target.value)}
                                            placeholder="Écrivez votre réponse ici ou sélectionnez un ton pour utiliser l'IA..."
                                        />
                                    </>
                                )}
                            </div>

                            {!isGenerating && (
                                <div className="relative flex p-3 justify-end items-end self-stretch bg-primary-light border border-light-gray border-t-0 rounded-bl rounded-br">
                                    <EmojiPickerInput
                                        value={content}
                                        onChange={setContent}
                                        inputRef={textareaRef}
                                        pickerPosition="bottom-0"
                                        buttonContent={
                                            <>
                                                <img
                                                    className="logo-emoji-picker"
                                                    src={logoEmojiPicker}
                                                    alt="logo emoji picker"
                                                />
                                                <span className="text-medium-dark-gray text-[13px] leading-[120%] font-normal">
                                                    Emoji
                                                </span>
                                            </>
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="w-full flex justify-between">
                        {isloading ? (
                            <div className="w-full h-[200px] flex justify-center items-center">
                                <img
                                    src={loader}
                                    alt="Chargement..."
                                    className="mt-[15px] animate-rotate w-[25px] h-[25px]"
                                />
                            </div>
                        ) : (
                            <>
                                <button
                                    onClick={onClose}
                                    className="
                                    flex
                                    py-1.5
                                    px-5
                                    justify-center
                                    items-center
                                    gap-1
                                    self-stretch
                                    rounded-full
                                    bg-dark-gray
                                    text-white
                                    text-small
                                    font-medium
                                    leading-[120%]
                                    text-center
                                    cursor-pointer
                                  "
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleSubmitResponse}
                                    className="
                                    flex
                                    h-8
                                    px-5
                                    py-1.5
                                    justify-center
                                    items-center
                                    gap-1
                                    rounded-full
                                    bg-primary
                                    cursor-pointer
                                    text-dark-gray
                                    text-small
                                    font-medium
                                    leading-[120%]
                                    text-center
                                  "
                                >
                                    Valider la réponse
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </ModalGenerateText>
    );
};

export default ModalResponse;
